import axiosInstance from "@/lib/axios";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useDebounce } from "react-use";

interface SnomedSearchParams {
  searchTerm?: string;
  eclExpression: string;
  enabled?: boolean;
  preloadOptions?: boolean;
}

export interface OntologyCode {
  system: string;
  code: string;
  display: string;
}

/**
 * Search for SNOMED codes by ECL expression and search term
 * @param searchTerm - The search term to search for
 * @param eclExpression - The ECL expression to search for. Examples:
 * - << 404684003 - Children of parent code 404684003
 * - << 248326004 OR 364712009 - Children of parent code 248326004 or 364712009
 * @param preloadOptions - Preload codes even if a search term is not provided
 * @param enabled - Whether to enable the query
 * @returns The SNOMED codes
 */
export const useSnomedSearch = ({
  searchTerm,
  eclExpression,
  preloadOptions = false,
  enabled = true,
}: SnomedSearchParams) => {
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  useDebounce(
    () => {
      setDebouncedSearchTerm(searchTerm);
    },
    500,
    [searchTerm],
  );
  return useQuery<OntologyCode[]>({
    queryFn: async () => {
      const response = await axiosInstance.get(
        "/ontology/codes/snomed/search",
        {
          params: {
            eclExpression,
            searchTerm: debouncedSearchTerm,
            // If preloadOptions is true, we will fetch up to 100 codes,
            // otherwise we will fetch 25
            limit: preloadOptions ? 100 : 25,
          },
        },
      );

      return response.data?.codes || [];
    },
    queryKey: [
      "ontologies",
      "snomed",
      "search",
      eclExpression,
      debouncedSearchTerm,
    ],
    enabled:
      enabled &&
      // If preloadOptions is true, we will fetch the codes even if the search term is not provided
      (preloadOptions ||
        Boolean(debouncedSearchTerm && debouncedSearchTerm.length > 1)),
    staleTime: Number.POSITIVE_INFINITY,
  });
};
